import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { faPenToSquare, faSparkles } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { toast } from 'sonner';

import { AddPageContent } from '@/components/page-editor/add-page-modal/add-page-content';
import {
  BLANK_PAGE_OPTION,
  BLOG_LAYOUT_HEADER,
} from '@/components/page-editor/add-page-modal/page-template-types';
import { AI_TOAST_MESSAGES } from '@/components/shared/ai-menu-constants';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/icon';
import { Separator } from '@/components/ui/separator';
import { MutedText, Paragraph } from '@/components/ui/typography';
import { useSectionTemplates } from '@/hooks/use-section-templates';
import {
  fetchSpecialPages,
  useSpecialPagesBeforeOpen,
} from '@/hooks/use-special-pages-before-open';
import { showAiCreditsExhaustedModal, useAiCreditsExhausted } from '@/lib/ai-credits-gate';
import http, { extractApiErrorMessage } from '@/lib/http';
import { buildSectionsData } from '@/lib/page-utils';
import { cmsPagesEditorIndexPath, cmsPagesEditorPath, cmsPageTemplatesPath } from '@/lib/routes';
import { cn, formatErrorMessage, navigateTo } from '@/lib/utils';
import { useAiOperationsStore } from '@/stores/ai-operations-store';
import { usePagesActions } from '@/stores/pages-store';
import type { SectionStructure } from '@/types/page';
import { PageType } from '@/types/page';
import type { SpecialPageMeta } from '@/types/shared';

const DEFAULT_PAGE_METADATA = {
  slots: {
    header: { visible: true, variant: 'default' },
    footer: { visible: true, variant: 'default' },
  },
};

interface Choice {
  key: string;
  icon: IconDefinition;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

function ChoiceGrid({ className, choices }: { className?: string; choices: Choice[] }) {
  return (
    <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', className)}>
      {choices.map(({ key, icon, title, description, onClick, disabled }) => (
        <button
          key={key}
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={cn(
            'group flex flex-col items-center gap-4 rounded-lg border border-border bg-background p-8 text-center transition-colors',
            'hover:border-primary/40 hover:bg-primary/5',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <FeaturedIcon icon={icon} size="lg" colorScheme="primary" variant="filled" />
          <div className="space-y-1">
            <Paragraph className="font-semibold">{title}</Paragraph>
            <MutedText className="text-sm leading-tight text-balance">{description}</MutedText>
          </div>
        </button>
      ))}
    </div>
  );
}

type Step = 'method' | 'templates';

interface AddBlogModalProps {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

export function AddBlogModal({ trigger, open, onOpenChange, onCloseAutoFocus }: AddBlogModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>('method');
  const [isCreating, setIsCreating] = useState(false);
  const [pendingTemplateCode, setPendingTemplateCode] = useState<string | undefined>();
  const [pendingSections, setPendingSections] = useState<SectionStructure[] | undefined>();

  const { createPage } = usePagesActions();
  const isAiStreaming = useAiOperationsStore((state) => state.isStreaming);
  const creditsExhausted = useAiCreditsExhausted();
  const { templatesByCode, isLoading: isLoadingSectionTemplates } = useSectionTemplates();
  const { specialPages, prepareOpen, close } = useSpecialPagesBeforeOpen();
  const hasBlogPosts = specialPages?.hasBlogPosts ?? false;
  const storedLayoutCode = specialPages?.blogLayoutCode ?? undefined;

  // Resolve the stored layout before opening so the dialog renders once on the
  // correct step instead of flashing the template picker while special pages load.
  const openWithResolvedStep = useCallback((resolved: SpecialPageMeta | undefined) => {
    setStep(resolved?.hasBlogPosts && resolved?.blogLayoutCode ? 'method' : 'templates');
    setIsOpen(true);
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        close();
        setIsOpen(false);
        onOpenChange?.(false);
        return;
      }
      void prepareOpen(openWithResolvedStep);
    },
    [close, onOpenChange, prepareOpen, openWithResolvedStep],
  );

  useEffect(() => {
    if (open === undefined) return;
    if (open) {
      void prepareOpen(openWithResolvedStep);
    } else {
      close();
      setIsOpen(false);
    }
    return close;
  }, [open, prepareOpen, openWithResolvedStep, close]);

  useEffect(() => {
    if (!isOpen) {
      setIsCreating(false);
      setPendingTemplateCode(undefined);
      setPendingSections(undefined);
      return;
    }
    // Prefill the locked layout only when posts exist; otherwise the fresh pick must win.
    if (step !== 'method' || !storedLayoutCode || !hasBlogPosts) return;
    let cancelled = false;
    void (async () => {
      try {
        const templatesResponse = await http.get<{
          data: Array<{ code: string; sectionsStructure: SectionStructure[] }>;
        }>(cmsPageTemplatesPath(), {
          params: { pageType: PageType.BLOG },
        });
        if (cancelled) return;

        const matched = templatesResponse.data.data?.find((t) => t.code === storedLayoutCode);
        if (!matched) {
          setStep('templates');
          return;
        }
        setPendingTemplateCode(storedLayoutCode);
        setPendingSections(matched.sectionsStructure);
      } catch {
        if (!cancelled) setStep('templates');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, step, storedLayoutCode, hasBlogPosts]);

  const ensureIndexExists = useCallback(async () => {
    const specialPages = await fetchSpecialPages().catch(() => undefined);
    if (specialPages?.hasBlogIndex) return;

    await http.post(cmsPagesEditorIndexPath(), {
      pageType: PageType.BLOG_INDEX,
      metadata: DEFAULT_PAGE_METADATA,
    });
  }, []);

  const createBlog = useCallback(async () => {
    const templateCode = pendingTemplateCode;
    const sectionsStructure = pendingSections;
    if (storedLayoutCode && (!templateCode || !sectionsStructure)) {
      toast.error('Blog layout is still loading. Please try again in a moment.');
      setIsCreating(false);
      return;
    }
    if (isAiStreaming) {
      toast.error(AI_TOAST_MESSAGES.OPERATION_IN_PROGRESS);
      setIsCreating(false);
      return;
    }
    if (isLoadingSectionTemplates) {
      toast.error('Section templates are still loading. Please try again in a moment.');
      setIsCreating(false);
      return;
    }

    setIsCreating(true);

    try {
      await ensureIndexExists();

      const sectionsData = sectionsStructure
        ? await buildSectionsData(sectionsStructure, templatesByCode)
        : undefined;

      await createPage(
        {
          pageType: PageType.BLOG,
          ...(templateCode && { pageTemplateCode: templateCode }),
          ...(sectionsData && { sections: sectionsData }),
          metadata: DEFAULT_PAGE_METADATA,
        },
        {
          onSuccess: () => handleOpenChange(false),
          onError: (errors) => {
            toast.error(formatErrorMessage(errors, 'Failed to create blog post'));
            setIsCreating(false);
          },
          onFinish: () => setIsCreating(false),
        },
      );
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Failed to create blog post');
      setIsCreating(false);
    }
  }, [
    ensureIndexExists,
    handleOpenChange,
    createPage,
    templatesByCode,
    isAiStreaming,
    isLoadingSectionTemplates,
    pendingTemplateCode,
    pendingSections,
    storedLayoutCode,
  ]);

  const createBlogWithAi = useCallback(async () => {
    if (creditsExhausted) {
      showAiCreditsExhaustedModal();
      return;
    }

    const templateCode = pendingTemplateCode;
    const sectionsStructure = pendingSections;
    if (storedLayoutCode && (!templateCode || !sectionsStructure)) {
      toast.error('Blog layout is still loading. Please try again in a moment.');
      return;
    }
    if (isAiStreaming) {
      toast.error(AI_TOAST_MESSAGES.OPERATION_IN_PROGRESS);
      return;
    }
    if (isLoadingSectionTemplates) {
      toast.error('Section templates are still loading. Please try again in a moment.');
      return;
    }

    setIsCreating(true);

    try {
      await ensureIndexExists();

      const sectionsData = sectionsStructure
        ? await buildSectionsData(sectionsStructure, templatesByCode)
        : undefined;

      const response = await http.post(cmsPagesEditorIndexPath(), {
        pageType: PageType.BLOG,
        ...(templateCode && { pageTemplateCode: templateCode }),
        ...(sectionsData && { sections: sectionsData }),
        metadata: DEFAULT_PAGE_METADATA,
      });
      const data = response.data as { pageId?: string };
      if (data.pageId) {
        navigateTo(`${cmsPagesEditorPath(data.pageId)}?assistantMode=blog_create`);
      }
      handleOpenChange(false);
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Failed to create blog post');
      setIsCreating(false);
    }
  }, [
    ensureIndexExists,
    handleOpenChange,
    templatesByCode,
    isAiStreaming,
    isLoadingSectionTemplates,
    pendingTemplateCode,
    pendingSections,
    storedLayoutCode,
    creditsExhausted,
  ]);

  const dialogSize = step === 'templates' ? '4xl' : 'xl';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent size={dialogSize} onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle>
            {step === 'templates' ? BLOG_LAYOUT_HEADER.title : 'Create Your Blog Post'}
          </DialogTitle>
          <DialogDescription>
            {step === 'method' &&
              'You can write your own or upload a document and let AI generate a summary.'}
            {step === 'templates' && BLOG_LAYOUT_HEADER.description}
          </DialogDescription>
        </DialogHeader>
        {step === 'templates' && <Separator className="mt-2" />}

        {step === 'method' && (
          <ChoiceGrid
            className="pt-2"
            choices={[
              {
                key: 'blank',
                icon: faPenToSquare,
                title: 'Write My Own',
                description: 'Write your own blog post',
                disabled: isCreating,
                onClick: () => createBlog(),
              },
              {
                key: 'ai',
                icon: faSparkles,
                title: 'Create With AI',
                description: 'Turn ideas into publish-ready blog posts with AI',
                disabled: isCreating,
                onClick: () => createBlogWithAi(),
              },
            ]}
          />
        )}

        {step === 'templates' && (
          <AddPageContent
            selectedItem={BLANK_PAGE_OPTION.key}
            templatesByCode={templatesByCode}
            isLoadingSectionTemplates={isLoadingSectionTemplates}
            onAddTemplate={(_pageType, templateCode, sectionsStructure) => {
              setPendingTemplateCode(templateCode);
              setPendingSections(sectionsStructure);
              setStep('method');
            }}
            isCreating={isCreating}
            childPageType={PageType.BLOG}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
